import React, { useState, useEffect, useCallback } from 'react';
import styles from './CategoryMatcherGame.module.css';
import plaidService, { Transaction } from '../services/plaidService';
import PlaidLink from '../components/PlaidLink';

const CATEGORY_OPTIONS = ['Need', 'Want', 'Savings', 'Income'];

interface Result {
  plaidId?: string;
  name?: string;
  category: string;
}

const CategoryMatcherGame: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gameTransactions, setGameTransactions] = useState<Transaction[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState<Result[]>([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [updatingDatabase, setUpdatingDatabase] = useState(false);
  const [databaseUpdated, setDatabaseUpdated] = useState(false);
  const [timeLeft, setTimeLeft] = useState(5);
  const [timerActive, setTimerActive] = useState(false);
  const [xpGained, setXpGained] = useState(0);
  const [recentXpChange, setRecentXpChange] = useState<number | null>(null);
  const [showXpFeedback, setShowXpFeedback] = useState(false);

  // XP management functions
  const addXP = (amount: number) => {
    const currentXP = parseInt(localStorage.getItem('user_xp') || '0');
    const newXP = currentXP + amount;
    localStorage.setItem('user_xp', newXP.toString());
    setXpGained(prev => prev + amount);
    
    // Show visual feedback
    setRecentXpChange(amount);
    setShowXpFeedback(true);
    
    // Hide feedback after 1.5 seconds
    setTimeout(() => {
      setShowXpFeedback(false);
      setRecentXpChange(null);
    }, 1500);
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Shorten transaction name to 3 words or less
  const shortenTransactionName = (name: string): string => {
    const words = name.split(' ');
    if (words.length <= 3) {
      return name;
    }
    return words.slice(0, 3).join(' ') + '...';
  };

  useEffect(() => {
    const loadTransactions = async () => {
      try {
        setLoading(true);
        const txs: Transaction[] = await plaidService.getPersistedTransactions();
        console.log('Total transactions loaded:', txs.length);
        
        // Only include transactions that haven't been categorized yet
        // Check for both missing budget_category and 'uncategorized' string value
        const uncategorizedTransactions = txs.filter(tx => 
          !tx.budget_category || tx.budget_category === 'uncategorized'
        );
        
        console.log('Uncategorized transactions for categorization:', uncategorizedTransactions.length);
        console.log('Income transactions:', uncategorizedTransactions.filter(tx => tx.amount < 0).length);
        console.log('Expense transactions:', uncategorizedTransactions.filter(tx => tx.amount > 0).length);
        setGameTransactions(uncategorizedTransactions);
      } catch (err) {
        setError('Failed to load transactions');
        console.error('Error loading transactions:', err);
      } finally {
        setLoading(false);
      }
    };
    loadTransactions();
  }, []);

  useEffect(() => {
    if (finished && !databaseUpdated) {
      const updateDatabase = async () => {
        try {
          setUpdatingDatabase(true);

          // Convert results to the format expected by the API, filtering out any without plaidId
          const categories = results
            .filter(r => r.plaidId) // Only include transactions with plaidId
            .map(r => ({
              plaidId: r.plaidId!,
              budget_category: r.category
            }));

          console.log('CategoryMatcher: Sending categories to database:', categories);
          console.log('CategoryMatcher: Savings categories being sent:', categories.filter(c => c.budget_category === 'Savings'));

          // Update the database
          const result = await plaidService.updateTransactionCategories(categories);
          console.log('Database update result:', result);
          
          // Save results to localStorage as a mapping from plaidId to category
          const mapping: Record<string, string> = {};
          results.forEach(r => {
            if (r.plaidId) {
              mapping[r.plaidId] = r.category;
            }
          });
          localStorage.setItem('category_matcher_results', JSON.stringify(mapping));
          
          setDatabaseUpdated(true);
          
          // Emit an event to notify parent component to refresh transaction data
          const refreshEvent = new CustomEvent('transactionsUpdated', {
            detail: { updatedCount: categories.length }
          });
          window.dispatchEvent(refreshEvent);
        } catch (err) {
          console.error('Error updating database:', err);
          setError('Failed to save categories to database. Please try again.');
        } finally {
          setUpdatingDatabase(false);
        }
      };
      
      updateDatabase();
    }
  }, [finished, results, databaseUpdated]);

  // Define handleTimeUp before using it in useEffect
  const handleTimeUp = useCallback(() => {
    setTimerActive(false);
    const currentTransaction = gameTransactions[currentIndex];
    
    // Deduct XP for timeout
    addXP(-5);
    
    // Auto-categorize as "Want" when time runs out
    setResults(prev => {
      const newResults = [...prev, { 
        plaidId: currentTransaction.plaidId, 
        name: currentTransaction.name, 
        category: 'Want' 
      }];
      return newResults;
    });
    
    if (currentIndex < gameTransactions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setFinished(true);
    }
  }, [gameTransactions, currentIndex]);

  // Handle PlaidLink success - refresh transactions
  const handlePlaidSuccess = useCallback(async () => {
    console.log('Bank account connected successfully, refreshing transactions...');
    setLoading(true);
    setError(null);
    
    try {
      const transactions = await plaidService.fetchTransactions();
      const uncategorizedTransactions = transactions.filter(t => 
        !t.budget_category || t.budget_category === 'uncategorized'
      );
      setGameTransactions(uncategorizedTransactions);
    } catch (err) {
      setError('Failed to load new transactions');
      console.error('Error loading transactions after bank connection:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Timer effect - start timer when game starts and reset for each new transaction
  useEffect(() => {
    if (gameStarted && !finished && currentIndex < gameTransactions.length) {
      setTimeLeft(5);
      setTimerActive(true);
    }
  }, [gameStarted, currentIndex, finished, gameTransactions.length]);

  // Timer countdown effect
  useEffect(() => {
    if (!timerActive || timeLeft <= 0) return;

    const timer = setTimeout(() => {
      if (timeLeft === 1) {
        // Time's up - auto-select "Want" as default and move to next
        handleTimeUp();
      } else {
        setTimeLeft(timeLeft - 1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft, timerActive, handleTimeUp]);

  const handleCategorySelect = (category: string) => {
    setTimerActive(false); // Stop the timer when user makes a selection
    const currentTransaction = gameTransactions[currentIndex];
    
    // Award XP for answering within time limit
    addXP(10);
    
    console.log(`CategoryMatcher: Selected "${category}" for transaction:`, {
      name: currentTransaction.name,
      amount: currentTransaction.amount,
      plaidId: currentTransaction.plaidId
    });
    
    setResults(prev => {
      const newResults = [...prev, { plaidId: currentTransaction.plaidId, name: currentTransaction.name, category }];
      console.log('CategoryMatcher: Updated results array:', newResults);
      return newResults;
    });
    
    if (currentIndex < gameTransactions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setFinished(true);
    }
  };

  if (loading) return (
    <div className={styles.matcherContainer}>
      <div>Loading transactions...</div>
    </div>
  );
  if (error) return (
    <div className={styles.matcherContainer}>
      <div>Error: {error}</div>
    </div>
  );
  if (gameTransactions.length === 0) return (
    <div style={{ textAlign: 'center', margin: '48px 0' }}>
      <h1 style={{ color: 'hsl(260,80%,74%)', fontSize: '1.8rem', fontWeight: 700, marginBottom: '1rem' }}>Category Matcher</h1>
      
      {/* Connect Bank Account Button */}
      <div style={{ margin: '1.5rem 0' }}>
        <PlaidLink onSuccess={handlePlaidSuccess} />
      </div>
      
      <div style={{ color: 'hsl(260,80%,74%)', fontSize: '1.1rem', fontWeight: 500 }}>
        No transactions available for categorization! 🎉
      </div>
      <div style={{ color: 'hsl(260,80%,74%)', fontSize: '0.95rem', marginTop: '0.5rem' }}>
        All your transactions are ready for analysis.
      </div>
    </div>
  );

  if (finished) {
    return (
      <div className={styles.finished}>
        {updatingDatabase ? (
          <div>
            <div>Saving your categories to the database...</div>
            <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
              Please wait while we update your transaction categories.
            </div>
          </div>
        ) : databaseUpdated ? (
          <div>
            <div style={{ color: '#059669', fontWeight: 'bold', marginBottom: '1rem' }}>
              ✅ Categories saved successfully!
            </div>
            <div style={{ marginBottom: '1rem' }}>Great job! You finished categorizing all transactions.</div>
            <div style={{ 
              fontSize: '1.2rem', 
              fontWeight: 'bold',
              color: xpGained >= 0 ? '#059669' : '#ef4444',
              textAlign: 'center'
            }}>
              🌟 Total XP Earned: {xpGained > 0 ? '+' : ''}{xpGained}
            </div>
          </div>
        ) : (
          <div>
            <div>Processing your categories...</div>
          </div>
        )}
      </div>
    );
  }

  // Show start screen if not started
  if (!gameStarted) {
    return (
      <div className={styles.matcherContainer}>
        <h1 className={styles.gameTitle}>Category Matcher</h1>
        
        {/* Connect Bank Account Button */}
        <div style={{ margin: '1.5rem 0' }}>
          <PlaidLink onSuccess={handlePlaidSuccess} />
        </div>
        
        <div style={{ marginBottom: 20, textAlign: 'center', color: '#666' }}>
          Categorize {gameTransactions.length} transactions
        </div>
        <button
          onClick={() => setGameStarted(true)}
          style={{
            marginTop: 32,
            background: 'hsl(260,80%,74%)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '1rem 2.5rem',
            fontSize: '1.2rem',
            fontWeight: 700,
            cursor: 'pointer',
            width: '100%',
            maxWidth: 300
          }}
        >
          Start
        </button>
      </div>
    );
  }

  const currentTransaction = gameTransactions[currentIndex];
  const isExpense = currentTransaction?.amount > 0;
  const isIncome = currentTransaction?.amount < 0;

  return (
    <div className={styles.matcherContainer}>
      <h1 className={styles.gameTitle}>Category Matcher</h1>
      
      {/* Timer and XP Display */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1rem',
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: timeLeft <= 2 ? '#ef4444' : '#059669'
      }}>
        ⏱️ {timeLeft}s
      </div>
      
      {/* XP Display */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1rem',
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: xpGained >= 0 ? '#059669' : '#ef4444',
        position: 'relative'
      }}>
        🌟 XP: {xpGained > 0 ? '+' : ''}{xpGained}
        
        {/* Recent XP Change Feedback */}
        {showXpFeedback && recentXpChange !== null && (
          <div 
            className={styles.xpFeedback}
            style={{
              color: recentXpChange > 0 ? '#059669' : '#ef4444'
            }}
          >
            {recentXpChange > 0 ? '+' : ''}{recentXpChange}
          </div>
        )}
      </div>
      
      <div className={styles.transactionCard}>
        <div className={styles.transactionName}>{shortenTransactionName(currentTransaction?.name || '')}</div>
        <div className={styles.transactionDate}>
          {formatDate(currentTransaction?.date || '')}
        </div>
        <div className={styles.transactionAmount}>
          <span className={isExpense ? styles.expenseAmount : styles.incomeAmount}>
            {isExpense && '-'}{formatCurrency(Math.abs(currentTransaction?.amount || 0))}
          </span>
          <span className={styles.transactionType}>
            {isIncome ? 'Income' : 'Expense'}
          </span>
        </div>
        <div className={styles.buttonRow}>
          {CATEGORY_OPTIONS.map(option => (
            <button
              key={option}
              className={styles.categoryButton}
              onClick={() => handleCategorySelect(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.progress}>
        {currentIndex + 1} / {gameTransactions.length}
      </div>
    </div>
  );
};

export default CategoryMatcherGame; 